(ns ui.editor2.sidebar-images
  "The editor2 sidebar with the photos of Unsplash. Its actions are in
   `ui.editor2.unsplash`.

   The photos are a grid of same sized tiles, so that nothing moves while
   photos load: skeleton tiles stand in for a loading page, and the
   dominant color of a photo fills its tile until the image arrives."
  (:require [ui.editor2.unsplash :as unsplash]))


(defn icon [selected?]
  [:svg.size-6
   {:xmlns "http://www.w3.org/2000/svg"
    :fill "none"
    :viewBox "0 0 24 24"
    :stroke-width "1.5"
    :stroke "currentColor"
    :class ["size-6" (when-not selected? "text-slate-400")]}
   [:path
    {:stroke-linecap "round"
     :stroke-linejoin "round"
     :d "m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"
     }]])

(defn images-entry
  [selected?]
  [:div
   {:class ["flex" "pt-3" "pb-3" "pl-2" "pr-2" "flex-col" "justify-center" "items-center" "gap-1" "rounded-lg" "cursor-pointer" "hover:bg-gray-600"
            (when selected?
              "bg-gray-800")]
    :on {:click [[:store/assoc-in [:editor2 :sidebar-panel] :images]]}}
   (icon selected?)
   [:span
    {:class ["text-center" "text-xs" "font-medium" (when-not selected? "text-slate-400")]}
    "Images"]])

(def focus-ring
  ["focus:outline-none" "focus-visible:ring-2" "focus-visible:ring-white"
   "focus-visible:ring-offset-2" "focus-visible:ring-offset-gray-800"])

(def link-css
  ["text-white" "underline" "decoration-slate-500" "underline-offset-2"
   "hover:decoration-white" "focus:outline-none" "focus-visible:ring-1"
   "focus-visible:ring-white" "rounded-sm"])

(defn external-link
  [href text]
  [:a {:href href
       :target "_blank"
       :rel "noopener noreferrer"
       :class link-css}
   text])

(defn search-icon
  [css]
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :fill "none"
         :viewBox "0 0 24 24"
         :stroke-width "2"
         :stroke "currentColor"
         :aria-hidden "true"
         :class css}
   [:path {:stroke-linecap "round"
           :stroke-linejoin "round"
           :d "m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"}]])

(defn plus-icon
  [css]
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :fill "none"
         :viewBox "0 0 24 24"
         :stroke-width "2.5"
         :stroke "currentColor"
         :aria-hidden "true"
         :class css}
   [:path {:stroke-linecap "round"
           :stroke-linejoin "round"
           :d "M12 4.5v15m7.5-7.5h-15"}]])

(defn warning-icon
  []
  [:svg {:xmlns "http://www.w3.org/2000/svg"
         :fill "none"
         :viewBox "0 0 24 24"
         :stroke-width "2"
         :stroke "currentColor"
         :aria-hidden "true"
         :class ["h-5" "w-5"]}
   [:path {:stroke-linecap "round"
           :stroke-linejoin "round"
           :d "M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"}]])

(defn close-button
  []
  [:button
   {:type "button"
    :aria-label "Close"
    :class (concat ["absolute" "top-1.5" "right-1.5" "rounded" "p-1"
                    "text-slate-400" "hover:text-white" "hover:bg-gray-700"]
                   focus-ring)
    :on {:click [[:unsplash/dismiss-credit]]}}
   [:svg {:xmlns "http://www.w3.org/2000/svg"
          :fill "none"
          :viewBox "0 0 24 24"
          :stroke-width "2"
          :stroke "currentColor"
          :aria-hidden "true"
          :class ["h-3.5" "w-3.5"]}
    [:path {:stroke-linecap "round"
            :stroke-linejoin "round"
            :d "M6 18 18 6M6 6l12 12"}]]])

(defn spinner
  [css]
  [:div {:class (concat ["rounded-full" "border-2" "border-white/30" "border-t-white"
                         "motion-safe:animate-spin"]
                        css)
         :aria-hidden "true"}])

(defn search-form
  [{:keys [query]}]
  ;; Stays on top while the photos scroll. The sidebar panel
  ;; (`ui.editor2.sidebar/panel`) has a padding of 24px (p-6) and a gap of
  ;; 16px (gap-4) to its header: `-top-6` covers the padding above the
  ;; stuck form, `-mt-4 pt-4` the gap, without covering the header.
  [:form
   {:role "search"
    :class ["sticky" "-top-6" "z-10" "-mt-4" "pt-4" "pb-3" "bg-gray-800"]
    :on {:submit [[:js/prevent-default]
                  [:unsplash/search]]}}
   [:label.sr-only {:for "unsplash-query"}
    "Search photos on Unsplash"]
   [:div.relative
    [:input
     {:type "search"
      :id "unsplash-query"
      :autocomplete "off"
      :placeholder "Search free photos"
      :value (or query "")
      :class ["w-full" "rounded-md" "border" "border-gray-600" "bg-gray-900"
              "py-2" "pl-3" "pr-9" "text-sm" "text-white" "placeholder-slate-400"
              "focus:outline-none" "focus:border-gray-400" "focus:ring-1"
              "focus:ring-gray-400"]
      :on {:input [[:store/assoc-in (conj unsplash/path :query) :event/target.value]]}}]
    [:button
     {:type "submit"
      :aria-label "Search"
      :class (concat ["absolute" "right-1" "top-1/2" "-translate-y-1/2" "rounded"
                      "p-1.5" "text-slate-400" "hover:text-white" "hover:bg-gray-700"]
                     focus-ring)}
     (search-icon ["h-4" "w-4"])]]
   [:p {:class ["mt-1.5" "text-[11px]" "leading-4" "text-slate-400"]}
    "Photos by "
    (external-link unsplash/unsplash-url "Unsplash")
    ", free to use under the "
    (external-link unsplash/license-url "Unsplash License")
    "."]])

(defn credit-card
  "The credit of the added photo, as Unsplash's API guidelines ask for."
  [{:keys [name profile-url instagram-username thumb]}]
  [:div
   {:role "status"
    :class ["relative" "mb-3" "flex" "gap-2.5" "rounded-lg" "bg-gray-900" "p-2"
            "pr-8" "ring-1" "ring-gray-700"]}
   (when thumb
     [:img {:src thumb
            :alt ""
            :class ["h-12" "w-9" "flex-none" "rounded" "object-cover"]}])
   [:div {:class ["min-w-0" "text-xs" "leading-5" "text-slate-300"]}
    [:p.font-medium.text-white "Added to your story"]
    [:p
     "Photo by "
     (external-link profile-url name)
     " on "
     (external-link unsplash/unsplash-url "Unsplash")]
    (when instagram-username
      [:p.text-slate-400
       "Credit on Instagram: "
       [:span {:class ["font-medium" "text-slate-200" "select-all"]}
        (str "@" instagram-username)]])]
   (close-button)])

(defn select-error-card
  [message]
  [:div
   {:role "alert"
    :class ["relative" "mb-3" "rounded-lg" "bg-red-950/60" "p-2" "pr-8" "text-xs"
            "leading-5" "text-red-200" "ring-1" "ring-red-900"]}
   message
   (close-button)])

(defn photo-tile
  [{:keys [adding-id]} photo]
  (let [adding? (= adding-id (:id photo))
        busy? (some? adding-id)]
    [:li {:replicant/key (:id photo)}
     [:button
      {:type "button"
       :title (str "Photo by " (:name photo))
       :aria-label (str "Add photo by " (:name photo))
       :aria-busy (str adding?)
       ;; Not `:disabled`: that would drop the keyboard focus of the
       ;; clicked tile. `select!` ignores clicks while a photo is added.
       :aria-disabled (str busy?)
       :class (concat ["group" "relative" "block" "w-full" "aspect-[3/4]"
                       "overflow-hidden" "rounded-lg" "bg-gray-900"
                       (if busy? "cursor-wait" "cursor-pointer")]
                      focus-ring)
       :style {:background-color (:color photo)}
       :on {:click [[:unsplash/select photo]]}}
      [:img {:src (or (:small photo) (:thumb photo))
             :alt ""
             :loading "lazy"
             :draggable "false"
             :class ["absolute" "inset-0" "h-full" "w-full" "object-cover"
                     "motion-safe:transition-transform" "motion-safe:duration-300"
                     (when-not busy? "group-hover:scale-105")]}]
      (if adding?
        [:div {:class ["absolute" "inset-0" "flex" "flex-col" "items-center"
                       "justify-center" "gap-2" "bg-black/55" "text-[11px]"
                       "font-medium" "text-white"]}
         (spinner ["h-6" "w-6"])
         "Adding…"]
        [:div {:class ["absolute" "inset-x-0" "bottom-0" "flex" "items-end" "gap-1"
                       "bg-gradient-to-t" "from-black/80" "via-black/40" "to-transparent"
                       "px-2" "pb-1.5" "pt-8" "text-left" "opacity-0"
                       "motion-safe:transition-opacity" "motion-safe:duration-150"
                       (when-not busy? "group-hover:opacity-100")
                       "group-focus-visible:opacity-100"]}
         [:span {:class ["min-w-0" "flex-1" "truncate" "text-[11px]" "font-medium"
                         "leading-4" "text-white"]}
          (:name photo)]
         [:span {:class ["flex" "h-5" "w-5" "flex-none" "items-center" "justify-center"
                         "rounded-full" "bg-white" "text-gray-900"]}
          (plus-icon ["h-3" "w-3"])]])]]))

(defn skeleton-tiles
  [n]
  (for [i (range n)]
    [:li {:replicant/key (str "skeleton-" i)
          :aria-hidden "true"}
     [:div {:class ["aspect-[3/4]" "rounded-lg" "bg-gray-700/60"
                    "motion-safe:animate-pulse"]}]]))

(defn message-state
  "A centered message instead of the photos, with an optional action."
  [{:keys [icon title text action]}]
  [:div {:class ["flex" "flex-col" "items-center" "rounded-lg" "border" "border-dashed"
                 "border-gray-600" "px-4" "py-8" "text-center"]}
   (when icon
     [:div {:class ["mb-3" "flex" "h-10" "w-10" "items-center" "justify-center"
                    "rounded-full" "bg-gray-900" "text-slate-400"]}
      icon])
   [:p {:class ["text-sm" "font-medium" "text-white"]} title]
   [:p {:class ["mt-1" "text-xs" "leading-5" "text-slate-400"]} text]
   (when-let [[label actions] action]
     [:button
      {:type "button"
       :class (concat ["mt-4" "rounded-md" "bg-white" "px-3" "py-1.5" "text-xs"
                       "font-semibold" "text-gray-900" "hover:bg-slate-200"]
                      focus-ring)
       :on {:click actions}}
      label])])

(defn list-heading
  [{:keys [mode search-query]}]
  [:h3 {:class ["mb-2" "truncate" "text-xs" "font-medium" "text-slate-300"]}
   (if (= mode :search)
     (str "Results for “" search-query "”")
     "Popular on Unsplash")])

(defn list-footer
  [{:keys [loading error mode search-query] :as unsplash}]
  (cond
    error
    [:div {:role "alert"
           :class ["mt-3" "flex" "items-center" "justify-between" "gap-2"
                   "rounded-md" "bg-gray-900" "px-3" "py-2" "text-xs" "text-slate-300"]}
     "More photos could not be loaded."
     [:button {:type "button"
               :class (concat ["flex-none" "rounded-sm" "font-semibold" "text-white"
                               "underline" "underline-offset-2"]
                              focus-ring)
               :on {:click [[:unsplash/retry]]}}
      "Try again"]]

    loading
    nil

    (unsplash/has-more? unsplash)
    [:button
     {:type "button"
      :class (concat ["mt-3" "w-full" "rounded-md" "border" "border-gray-600"
                      "py-2" "text-xs" "font-medium" "text-slate-200"
                      "hover:bg-gray-700" "hover:border-gray-500"]
                     focus-ring)
      :on {:click [[:unsplash/load-more]]}}
     "Load more photos"]

    (= mode :search)
    [:p {:class ["mt-3" "text-center" "text-[11px]" "text-slate-400"]}
     (str "That’s all for “" search-query "”.")]))

(defn photo-list
  [{:keys [photos loading error mode search-query] :as unsplash}]
  (cond
    (and error (empty? photos))
    (message-state {:icon (warning-icon)
                    :title "Photos could not be loaded"
                    :text (unsplash/error-message (:status error))
                    :action ["Try again" [[:unsplash/retry]]]})

    (or (= loading :initial)
        (and (empty? photos) (nil? mode)))
    [:ul {:class ["grid" "grid-cols-2" "gap-2"]
          :aria-busy "true"}
     (skeleton-tiles 8)]

    (empty? photos)
    (message-state {:icon (search-icon ["h-5" "w-5"])
                    :title (str "No photos for “" search-query "”")
                    :text "Try a broader word, like “beach” or “city”."
                    :action ["Show popular photos"
                             [[:store/assoc-in (conj unsplash/path :query) ""]
                              [:unsplash/search]]]})

    :else
    [:div
     [:ul {:class ["grid" "grid-cols-2" "gap-2"]
           :aria-busy (str (= loading :more))}
      (for [photo photos]
        (photo-tile unsplash photo))
      (when (= loading :more)
        (skeleton-tiles 4))]
     (list-footer unsplash)]))

(defn unsplash-panel
  "The panel for the `unsplash` state (see `ui.editor2.unsplash`)."
  [{:keys [credit select-error] :as unsplash}]
  [:div#unsplash-search {:class ["w-full"]}
   (search-form unsplash)
   (cond
     select-error (select-error-card select-error)
     credit (credit-card credit))
   (list-heading unsplash)
   (photo-list unsplash)])

(defn panel
  [state]
  [:div
   {:replicant/key :unsplash-panel
    :replicant/on-mount [[:unsplash/init]]
    :class ["flex" "flex-col" "w-full"]}
   (unsplash-panel (get-in state unsplash/path))])
